import React, {useEffect, useMemo, useRef, useState} from 'react';
import { AuthSkyline } from '../components/common/AuthSkyline';
import { Helmet } from 'react-helmet-async';
import { PencilFill, FunnelFill, InfoCircleFill, XCircleFill, ArrowLeftCircle, ArrowRightCircle } from 'react-bootstrap-icons'; // 1. Import an icon
import {FaTimes} from 'react-icons/fa';
import {
  BannerText,
  BannerIconButton,
  BannerContent,
  BannerSubtext,
  InfoCircleMod,
  SearchBarInput, 
  SearchBarWrapper,
  GhostText,
  TagWrap,
  Tag,
  RemoveTagButton,
  ContentArea, 
  DisplayArea,
  SearchArea,
  GapCover,
  PageSelectArea,
  BottomGapCover,
  SearchPageShell2,
  InfoBanner2,
  ResultsGrid2,
  LoadingOverlay,
  LoadingTopLayer,
  NoResultsMessage,
  Dialog,
  Overlay,
} from '../styles/search/search.style';
import { Link } from 'react-router-dom';
import { SUBJECT_OPTIONS, UserDetails, AvailabilityObject, ITEMS_PER_PAGE} from '../types/schemas/abc';
import { useTutors } from '../hooks/useTutors';
import { TutorCard } from '../components/search/TutorCard';
import { useUser } from '../contextManager/user.context';
import { SimilaritySidebar } from '../components/search/SimilaritySidebar';
import { TutorDetailView } from '../components/search/TutorDetailView';
import { ClipLoader } from 'react-spinners'; // CHANGED: Import ClipLoader
import { useToast } from '../contextManager/toast.context';
import { ParentTip } from '../components/register/InfoBlock';
import { calculateExpertiseMatch, calculateScheduleOverlap } from '../utils/searchHelpers';
import { useScale } from './HeightScaler';
import { NAVIGATION_HEIGHT } from '../constants/global';
import { Area } from '../styles/_app.style';
import { createPortal } from 'react-dom';
import { TuteeLoader } from '../components/common/TuteeLoader';
import { BookLoader } from '../components/common/BookLoader';
import { getParentProfileStatus } from '../utils';
import { IncompleteProfileOverlay } from '../components/search/UnverifiedAccountOverlay';

interface SearchPageProps { 
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const PADDING_VALUE = '1rem';


const SearchPage: React.FC<SearchPageProps> = ({scrollContainerRef}) => {

    const [isFiltering, setIsFiltering] = useState(false);
    const [subjectInput, setSubjectInput] = useState('');
    const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
    const [isSidebarOverflowing, setIsSidebarOverflowing] = useState(false);

    const scrollPositionRef = useRef(0);

    const [expandedTutor, setExpandedTutor] = useState<UserDetails | null>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const { userDetails: parentDetails } = useUser();

    const parentRequiredSubjects = useMemo(() => {
      const fromExpertise = parentDetails?.expertise ? Object.keys(parentDetails.expertise) : [];
      const fromSubjects = parentDetails?.subjects || [];
      return fromExpertise.length > 0 ? fromExpertise : fromSubjects;
  }, [parentDetails]);

  const subjectsToFetch = useMemo(() => {
    // If the user has actively selected any subjects for filtering,
    // use that list for the API call. Otherwise, use the parent's default subjects.
    return selectedSubjects.length > 0 
        ? selectedSubjects 
        : parentRequiredSubjects;
}, [parentRequiredSubjects, selectedSubjects]);

    const {data: tutors, isLoading, isError} = useTutors(subjectsToFetch);

    const [hoveredTutor, setHoveredTutor] = useState<UserDetails | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [sidebarTop, setSidebarTop] = useState<number | null>(null);

    const displayAreaRef = useRef<HTMLDivElement | null>(null);
    const [sortedTutors, setSortedTutors] = useState<UserDetails[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const sidebarFinalState = isSidebarOpen && !isSidebarOverflowing ? 'open' : 'closed';

    const {addToast} = useToast();

    const {scale} = useScale();

    useEffect(() => {
      if (isError) {
        addToast({
          variant: 'error', 
          title: 'Error Fetching Tutors',
          // message: 'There was a problem loading tutor data. Please try again.'
        })
      }

    }, [isError, addToast])

    const filteredTutors = useMemo(() => {
      if (!tutors) return [];
      
      if (selectedSubjects.length === 0) {
        return tutors;
      }
  
      return tutors.filter(tutor => {
        return selectedSubjects.some(selectedSubject =>
          tutor.subjects?.includes(selectedSubject)
        );
      });
    }, [tutors, selectedSubjects]);

    useEffect(() => {
      if (!filteredTutors || !parentDetails) return;

      const minLoadingTime = 2000; 
      const startTime = Date.now();
      
      setIsProcessing(true);
    
    
      // Perform the sort after a short delay to allow the UI to update
      const sortTimeout = setTimeout(() => {
        const sorted = [...filteredTutors].sort((tutorA, tutorB) => {
          const matchesA = calculateExpertiseMatch(parentDetails, tutorA);
          const overlapA = calculateScheduleOverlap(parentDetails, tutorA);
          const matchesB = calculateExpertiseMatch(parentDetails, tutorB);
          const overlapB = calculateScheduleOverlap(parentDetails, tutorB);
          if (matchesA !== matchesB) return matchesB - matchesA;
          return overlapB - overlapA;
        });
    
        setSortedTutors(sorted);
        const elapsedTime = Date.now() - startTime; 
        const remainingTime = minLoadingTime - elapsedTime; 
        setTimeout(() => {
          setIsProcessing(false);
        }, Math.max(0, remainingTime));

      }, 100);
    
      // Simple cleanup
      return () => clearTimeout(sortTimeout);
    
    }, [filteredTutors, parentDetails]);
    
    const { isComplete: isParentProfileComplete, missingFields } = useMemo(
      () => getParentProfileStatus(parentDetails),
      [parentDetails]
    );

    useEffect(() => {
      if (!isParentProfileComplete) return;
      if (isProcessing || isLoading) {
        document.body.classList.add('search-loading-active');
      } else {
        document.body.classList.remove('search-loading-active');
      }

      return () => {
        document.body.classList.remove('search-loading-active');
      }
    }, [isProcessing, isLoading, isParentProfileComplete]);


    const paginatedTutors = useMemo(() => {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      return sortedTutors.slice(startIndex, endIndex);
    }, [sortedTutors, currentPage]);

    const totalPages = Math.ceil(sortedTutors.length / ITEMS_PER_PAGE);
    useEffect(() => {
      if (currentPage > totalPages) setCurrentPage(totalPages);
      if (currentPage < 1) setCurrentPage(1);
    }, [totalPages]); // eslint-disable-line react-hooks/exhaustive-deps
    
    // 3) Helpers
    const canPrev = currentPage > 1;
    const canNext = currentPage < totalPages;
    const displayCurrent = sortedTutors.length === 0 ? 0 : currentPage; // show 0 of 1 for empty

    const handleNextPage = () => {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }

    const handlePrevPage = () => {
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, tutor: UserDetails) => {
      const cardEl = e.currentTarget;
      const container = displayAreaRef.current;
      if (container) {
        const cardRect = cardEl.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const cardTop = cardRect.top / scale;
        const containerTop = containerRect.top / scale;
        const topInside = cardTop - containerTop + container.scrollTop;
        setSidebarTop(topInside);
      }
      setHoveredTutor(tutor);
      setIsSidebarOpen(true);
    };
    
    const handleMouseLeave = () => {
      setIsSidebarOpen(false); 
    };

    const handleCardClick = (tutor: UserDetails) => {
      if (scrollContainerRef.current) { 
        scrollPositionRef.current = scrollContainerRef.current.scrollTop; 
      }
      setExpandedTutor(tutor);
      setIsSidebarOpen(false);
      setHoveredTutor(null);
    };

    useEffect(() => {
      const container = scrollContainerRef.current; 
      if (!container) return; 

      if (expandedTutor) {
        container.scrollTo({top: 0, behavior: 'auto'});
      } else {
        container.scrollTo({top: scrollPositionRef.current, behavior: 'auto'})
      }
    }, [expandedTutor]);

    useEffect(() => {
      const container = scrollContainerRef.current; 
      if (!container) return; 
      container.scrollTo({top: 0, behavior: 'auto'});


    }, [currentPage])

    const suggestion = useMemo(() => 
      SUBJECT_OPTIONS
        .filter(opt => !selectedSubjects.includes(opt)) // Exclude already selected tags
        .find(opt => opt.toLowerCase().startsWith(subjectInput.toLowerCase())) || ''
    , [subjectInput, selectedSubjects]);

    const addSubject = (subjectToAdd: string) => {
      if (subjectToAdd && !selectedSubjects.includes(subjectToAdd)) {
        setSelectedSubjects(prev => [...prev, subjectToAdd]);
      }
      setSubjectInput(''); // Clear input after adding
    };
    const removeSubject = (subjectToRemove: string) => {
      setSelectedSubjects(prev => prev.filter(s => s !== subjectToRemove));
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => { // Changed HTMLInputElement to HTMLDivElement
      if (['Enter','Tab'].includes(e.key) && suggestion) {
        e.preventDefault();
        addSubject(suggestion);
      }
    };
    const handleOpenFilter = () => setIsFiltering(true);
    const handleCloseFilter = () => setIsFiltering(false);
    const showNoResults = !isLoading && !isProcessing && sortedTutors.length === 0;
    const expandedBottomRem = `calc(100vh / ${scale} - (${NAVIGATION_HEIGHT} + ${PADDING_VALUE} + 1rem))`;
    const nonExpandedBottomRem = `calc(100dvh - (${NAVIGATION_HEIGHT} + ${PADDING_VALUE} + 8rem))`;

    const closeProfileModal = () => {
      setExpandedTutor(null);
    }

    if (!isParentProfileComplete) {
      return (
        <Area css={{ height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`, marginTop: `calc(${NAVIGATION_HEIGHT})`, position: 'relative' }}>
        <AuthSkyline light />
          <IncompleteProfileOverlay missingFields={missingFields} />
        </Area>
      );
    }

    return (
      <Area css={{ height: `calc((100dvh / ${scale}) - ${NAVIGATION_HEIGHT})`, marginTop: `calc(${NAVIGATION_HEIGHT})`, position: 'relative' }}>
    <AuthSkyline light />
    <>
      <BottomGapCover/>
              <>
          <GapCover style={{bottom: nonExpandedBottomRem}} />
            <Helmet>
              <title>Search Tutors - tute</title>
            </Helmet>
            <SearchPageShell2>

            {(isLoading || isProcessing) && (
                              <LoadingOverlay>
                                {/* <TuteeLoader /> */}
                                <TuteeLoader/>
                            </LoadingOverlay>
                            )}

        {showNoResults && (
          <NoResultsMessage css={{ textAlign: 'center', gap: '1rem' }}>
            <svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="38" cy="38" r="22" fill="var(--colors-sand2, #fafafa)" stroke="var(--colors-sand7, #d4d4d4)" strokeWidth="2.5"/>
              <circle cx="38" cy="38" r="15" fill="var(--colors-blue2, #eff6ff)" stroke="var(--colors-blue6, #bfdbfe)" strokeWidth="1.5" strokeDasharray="4 3"/>
              <text x="38" y="44" textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--colors-blue9, #3b82f6)" fontFamily="sans-serif">?</text>
              <line x1="54" y1="54" x2="72" y2="72" stroke="var(--colors-sand8, #a3a3a3)" strokeWidth="3.5" strokeLinecap="round"/>
              <circle cx="72" cy="72" r="5" fill="var(--colors-sand6, #d4d4d4)"/>
              <circle cx="14" cy="22" r="2" fill="var(--colors-blue5)" opacity="0.4"/>
              <circle cx="68" cy="18" r="2.5" fill="var(--colors-amber4)" opacity="0.5"/>
              <circle cx="72" cy="40" r="1.5" fill="var(--colors-blue5)" opacity="0.3"/>
            </svg>
            <div>
              <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '0.95rem', color: 'var(--colors-sand12)' }}>
                No tutors found
              </p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--colors-sand10)', lineHeight: 1.4 }}>
                Try adjusting your search filters or broadening your subject selection.
              </p>
            </div>
          </NoResultsMessage>
        )}
              <ContentArea> 
                  <SearchArea>
                  <ParentTip/>
              {isFiltering ? (
                  <>
              <InfoBanner2>
              <SearchBarWrapper>
                    {/* The Ghost Text sits behind the input */}
                    {subjectInput && suggestion && (
                      <GhostText>
                        {subjectInput}{suggestion.slice(subjectInput.length)}
                      </GhostText>
                    )}
      
                    {/* The actual, visible input the user types in */}
                    <SearchBarInput
                      type="search"
                      placeholder="Search by subject and press Enter..."
                      value={subjectInput}
                      onChange={(e) => setSubjectInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                    />
                      </SearchBarWrapper>
              
                      <BannerIconButton onClick={handleCloseFilter}>
                          <XCircleFill size={18} />
                      </BannerIconButton>
              
                          </InfoBanner2>
                          </>
                      ) : (
                          <>
                      <InfoBanner2> 
                          <BannerContent>
                          <BannerText>Showing top matches for you.</BannerText>
                          <BannerSubtext>
                          <InfoCircleMod/> 
                          <span>
                      
                            By default, tutors are ordered by how far they match your <Link to="/profile">profile</Link>. Click the icon to filter by subject.
                          </span>
                          
                          </BannerSubtext>
                        </BannerContent>
              
                        <BannerIconButton onClick={handleOpenFilter}>
                          <FunnelFill size={18} />
                        </BannerIconButton>
                        </InfoBanner2>
              
                          </>

                        ) }

                        {selectedSubjects.length > 0 && (
                          <TagWrap display={true}  style={{justifyContent: 'flex-start'}}>
                            {selectedSubjects.map(subject => (
                              <Tag key={subject}>
                                {subject}
                                <RemoveTagButton onClick={() => removeSubject(subject)}>
                                  <FaTimes size={10} />
                                </RemoveTagButton>
                              </Tag>
                            ))}
                          </TagWrap>
                        )}
                        </SearchArea>
                        <DisplayArea ref={displayAreaRef}>
                              {tutors && 
                                <ResultsGrid2>
                                  {paginatedTutors.map((tutor: UserDetails) => (
                                    <div
                                      key={tutor.id}
                                      onClick={() => handleCardClick(tutor)}
                                      onMouseEnter={(e) => handleMouseEnter(e, tutor)}
                                      onMouseLeave={handleMouseLeave}
                                    >
                                      <TutorCard tutor={tutor} parent={parentDetails!} />
                                    </div>
                                  ))}
                                </ResultsGrid2>
                              
                            }
                          
                
                          {parentDetails && hoveredTutor && (
                            <SimilaritySidebar 
                              parent={parentDetails} 
                              tutor={hoveredTutor} 
                              isOpen={isSidebarOpen}
                              topPosition={sidebarTop}
                              onOverflowChange={setIsSidebarOverflowing} // The child will call this to update our state
                              dataState={sidebarFinalState} // Pass the final calculated state down

                            />
                            )}
                        </DisplayArea>
                
                        <PageSelectArea>
                          <InfoBanner2 style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                            {/* Left arrow (or placeholder to keep spacing) */}
                            <BannerIconButton
                              onClick={canPrev ? handlePrevPage : undefined}
                              disabled={!canPrev}
                              aria-hidden={!canPrev}
                              tabIndex={canPrev ? 0 : -1}
                              style={{ visibility: canPrev ? 'visible' : 'hidden' }}
                            >
                              <ArrowLeftCircle size={18} />
                            </BannerIconButton>

                            <span style={{ fontWeight: 600 }}>
                              Page {displayCurrent} of {totalPages}
                            </span>

                            {/* Right arrow (or placeholder) */}
                            <BannerIconButton
                              onClick={canNext ? handleNextPage : undefined}
                              disabled={!canNext}
                              aria-hidden={!canNext}
                              tabIndex={canNext ? 0 : -1}
                              style={{ visibility: canNext ? 'visible' : 'hidden' }}
                            >
                              <ArrowRightCircle size={18} />
                            </BannerIconButton>
                          </InfoBanner2>
                          </PageSelectArea>
                        </ContentArea>
                        </SearchPageShell2>

                
                    </>

                    {expandedTutor && createPortal(
                      <Overlay onClick={closeProfileModal} style={{ zIndex: 9999 }}>
                        {/* Stop propagation so clicking the card doesn't close the modal */}
                        <Dialog onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', height: '90vh', overflow: 'hidden', padding: 0, background: 'transparent', boxShadow: 'none', display: 'flex' }}>
                          
                            <TutorDetailView
                              tutor={expandedTutor}
                              parent={parentDetails!}
                              onClose={() => setExpandedTutor(null)} 
                            />
                          
                          
                        </Dialog>
                      </Overlay>,
                      document.body
                    )}
                  
                </>   
                </Area>
              )
            };

export default SearchPage;